import { FaroTooltip } from "@faro-lotv/flat-ui";
import { Box, ButtonBase } from "@mui/material";
import { Stack } from "@mui/system";
import { DateTime } from "luxon";
import { useMemo, type JSX } from "react";
import { groupBucketsByMonth } from "../capture-date-stats";
import { CalendarHeader } from "./calendar-header";
import { bandSx, cellSx, rangeRoleOf } from "./calendar-styles";
import {
  captureLabel,
  releaseTouchCapture,
  useBucketCounts,
  usePageTurnOnRest,
} from "./calendar-utils";
import { CaptureGranularity, ViewProps } from "./capture-calendar-types";
import { CaptureCountChip } from "./capture-markers";

const MONTHS_PER_YEAR = 12;
const MONTHS_PER_ROW = 3;

/** @returns A year of oval month cells, each showing how many captures it holds */
export function MonthGrid({
  stats,
  startDate,
  endDate,
  onPick,
  onHover,
  onPress,
  isDragging,
  isPickable,
  month,
  onMonthChange,
  bounds,
  onToggleView,
}: ViewProps): JSX.Element {
  const months = useMemo(() => groupBucketsByMonth(stats.days), [stats.days]);
  const counts = useBucketCounts(months);
  const year = month.startOf("year");
  const thisMonth = DateTime.now().startOf("month");
  const pageTurn = usePageTurnOnRest(isDragging);
  const isPreviousDisabled = year <= bounds.min.startOf("year");
  const isNextDisabled = year >= bounds.max.startOf("year");
  function previousYear(): void {
    onMonthChange(month.minus({ years: 1 }));
  }
  function nextYear(): void {
    onMonthChange(month.plus({ years: 1 }));
  }

  return (
    <Stack sx={{ userSelect: "none" }}>
      <CalendarHeader
        label={year.toFormat("yyyy")}
        granularity={CaptureGranularity.month}
        onToggleView={onToggleView}
        onPrevious={previousYear}
        onNext={nextYear}
        isPreviousDisabled={isPreviousDisabled}
        isNextDisabled={isNextDisabled}
        isDragging={isDragging}
      />

      <Box
        component="div"
        onPointerDown={releaseTouchCapture}
        sx={{
          display: "grid",
          gridTemplateColumns: `repeat(${MONTHS_PER_ROW}, 1fr)`,
          rowGap: 1,
          // A finger drags a range rather than scrolling the page
          touchAction: "none",
        }}
      >
        {Array.from({ length: MONTHS_PER_YEAR }, (_, index) => {
          const cell = year.plus({ months: index });
          const count = counts.get(cell.toMillis()) ?? 0;
          const role = rangeRoleOf(cell, "month", startDate, endDate);
          const column = index % MONTHS_PER_ROW;
          const isDisabled = cell > bounds.max || !isPickable(cell, "month");

          return (
            <FaroTooltip
              key={cell.toMillis()}
              placement="top"
              title={`${cell.toFormat("LLLL yyyy")} — ${captureLabel(count)}`}
              sx={[
                bandSx(role, column === 0, column === MONTHS_PER_ROW - 1),
                { px: 0.75 },
              ]}
            >
              <ButtonBase
                aria-label={`${cell.toFormat("LLLL yyyy")} ${captureLabel(count)}`}
                aria-pressed={role.isSelected}
                disabled={isDisabled}
                onClick={(event) => onPick(cell, "month", event.shiftKey)}
                onPointerEnter={() => {
                  onHover?.([cell, "month"]);
                  // January and December are the page's edges
                  if (index === 0 && !isPreviousDisabled) {
                    pageTurn.rest(previousYear);
                  } else if (index === MONTHS_PER_YEAR - 1 && !isNextDisabled) {
                    pageTurn.rest(nextYear);
                  }
                }}
                onPointerLeave={() => {
                  onHover?.();
                  pageTurn.leave();
                }}
                onPointerDown={() => onPress?.([cell, "month"])}
                sx={[
                  cellSx(role, cell.hasSame(thisMonth, "month"), isDisabled),
                  {
                    width: "100%",
                    height: "34px",
                    gap: 0.75,
                    fontSize: "13px",
                  },
                ]}
              >
                {cell.toFormat("LLL")}
                {count > 0 && <CaptureCountChip count={count} />}
              </ButtonBase>
            </FaroTooltip>
          );
        })}
      </Box>
    </Stack>
  );
}
